import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, Flex, Text, Input } from "@chakra-ui/react";
import { FocusLibraryItem } from "./FocusLibraryItem";
import { FocusDTO } from "@/js/api/focus";
import { sortFocusItemsByLastUsed, sortFocusItemsByNameAtoZ, sortFocusItemsByNameZtoA } from "./utils";
import { useFocusStore } from "@/stores/v2/focusStore";
import { ClasswizeSelect, ClasswizeSelectOption } from "@/components/ui/Select";

interface Props {
	hideDeleteOption?: boolean;
	handleClickFocusResource?: (focus: FocusDTO) => void;
}

export enum SortingMethod {
	A_TO_Z = "atoz",
	Z_TO_A = "ztoa",
	LAST_USED = "lastused",
}

const sortingOptions: ClasswizeSelectOption<SortingMethod>[] = [
	{ label: "A to Z", value: SortingMethod.A_TO_Z },
	{ label: "Z to A", value: SortingMethod.Z_TO_A },
	{ label: "Last Used", value: SortingMethod.LAST_USED },
];

export function FocusesList(props: Props): JSX.Element {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [sortingMethod, setSortingMethod] = useState<SortingMethod>(SortingMethod.LAST_USED);

	const focusStore = useFocusStore();

	const [filteredSortedFocuses, setFilteredSortedFocuses] = useState<FocusDTO[]>([]);

	useEffect(() => {
		let sortedFocuses: FocusDTO[];

		switch (sortingMethod) {
			case SortingMethod.LAST_USED:
				sortedFocuses = sortFocusItemsByLastUsed(focusStore.getLibraryItems());
				break;
			case SortingMethod.Z_TO_A:
				sortedFocuses = sortFocusItemsByNameZtoA(focusStore.getLibraryItems());
				break;
			case SortingMethod.A_TO_Z:
			default:
				sortedFocuses = sortFocusItemsByNameAtoZ(focusStore.getLibraryItems());
		}

		const filteredFocuses = sortedFocuses.filter((focus) => focus.name.toLowerCase().includes(searchTerm.toLowerCase()));

		setFilteredSortedFocuses(filteredFocuses);
	}, [searchTerm, sortingMethod, focusStore]);

	function handleChangeSearchTerm(event: ChangeEvent<HTMLInputElement>) {
		setSearchTerm(event.target.value);
	}

	return (
		<Flex direction={"column"} minHeight={"100%"} maxHeight={"100%"}>
			<Flex gap={3}>
				<Input placeholder={"Search by Focus name"} value={searchTerm} onChange={handleChangeSearchTerm} maxLength={30} />
				<ClasswizeSelect
					options={sortingOptions}
					value={sortingMethod}
					onChange={(value) => setSortingMethod(value as SortingMethod)}
					placeholder={"Sort by"}
					width="200px"
				/>
			</Flex>
			<Box overflow={"auto"} minHeight={"100%"} my={4} flex={1} pos={"relative"}>
				<Box pos={"absolute"} top={0} left={0} height={"100%"} width={"100%"}>
					{filteredSortedFocuses.map((focus) => (
						<Box key={focus.id}>
							<FocusLibraryItem
								id={focus.id}
								name={focus.name}
								active={focus.active}
								hideDeleteOption={!!props.hideDeleteOption}
								areTabsLocked={!!focus.tabsLocked}
								handleClickFocusItem={() => {
									props.handleClickFocusResource?.(focus);
								}}
							/>
						</Box>
					))}
					{filteredSortedFocuses.length === 0 && (
						<Text textAlign={"center"} mt={150}>
							No focuses found that match &quot;{searchTerm}&quot;
						</Text>
					)}
				</Box>
			</Box>
		</Flex>
	);
}
